import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface LocalUpdate<Update> {
  id: number
  addressKey: string
  update: Update
}

interface Pending<Address, Update> {
  address: Address
  addressKey: string
  updates: LocalUpdate<Update>[]
}

export interface PreferenceControllerOptions<Address, Value, Update> {
  address: Address
  /** Stable identity for the full preference address. */
  addressKey: string
  stored: Value
  writable: boolean
  /** Zero commits immediately; a positive value batches gestures for this many milliseconds. */
  debounceMs: number
  reduce: (value: Value, update: Update) => Value
  commit: (address: Address, updates: readonly Update[]) => Promise<void>
}

export interface PreferenceController<Value, Update> {
  value: Value
  update: (update: Update) => void
}

/**
 * Local-leading, debounced preference behavior without a transport.
 *
 * Updates retain the address at which the gesture occurred. Local changes are
 * overlays on the latest stored value rather than a private document snapshot,
 * so a sibling controller's successful write remains visible. The host decides
 * whether a settings read is writable and supplies the commit door.
 */
export function usePreferenceController<Address, Value, Update>({
  address, addressKey, stored, writable, debounceMs, reduce, commit,
}: PreferenceControllerOptions<Address, Value, Update>): PreferenceController<Value, Update> {
  const [local, setLocal] = useState<LocalUpdate<Update>[]>([])
  const nextId = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<Pending<Address, Update> | null>(null)
  const commitRef = useRef(commit)
  commitRef.current = commit

  const value = useMemo(() => local
    .filter((entry) => entry.addressKey === addressKey)
    .reduce((current, entry) => reduce(current, entry.update), stored),
  [addressKey, local, reduce, stored])

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    const batch = pending.current
    pending.current = null
    if (!batch) return
    const ids = new Set(batch.updates.map((entry) => entry.id))
    void commitRef.current(batch.address, batch.updates.map((entry) => entry.update))
      .then(() => setLocal((current) => current.filter((entry) => !ids.has(entry.id))))
      .catch(() => undefined)
  }, [])

  const update = useCallback((change: Update) => {
    const entry = { id: nextId.current++, addressKey, update: change }
    setLocal((current) => [...current, entry])
    // Fixture mode and an unread/refused settings bag remain local. In
    // particular, never manufacture a partial whole-key document from an
    // unknown base.
    if (!writable) return

    // A second address must not steal or retarget the first address's timer.
    if (pending.current && pending.current.addressKey !== addressKey) flush()
    const current = pending.current
    pending.current = current
      ? { ...current, updates: [...current.updates, entry] }
      : { address, addressKey, updates: [entry] }
    if (debounceMs === 0) {
      flush()
      return
    }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, debounceMs)
  }, [address, addressKey, debounceMs, flush, writable])

  // Match the existing preference hooks: an unmounted surface drops a gesture
  // whose debounce has not settled yet. Address changes do not unmount, and the
  // captured address above keeps those writes correctly scoped.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    pending.current = null
  }, [])

  return { value, update }
}
